import { SELECTION_DIRECTION, SelectionDirection } from "@/src/shared/types/selectionDirection";

/**
 * Moves connected DOM nodes to a new position in one synchronous step.
 *
 * Browsers fire `disconnectedCallback`/`connectedCallback` for every custom element inside a moved subtree, and some
 * elements (Turbo frames) treat that disconnect as a real removal. While a move made through `moveNodesBefore` is in
 * progress, the moved roots are marked so adapters (see `hotwire/turboFrameMovePreservation.ts`) can tell it apart
 * from a real removal via `isNodeInPreservedMove`. Focus and text selection inside the moved nodes are restored.
 */

type SelectionSnapshot = {
  start: number | null;
  end: number | null;
  direction: SelectionDirection;
};

type FocusSnapshot = {
  element: HTMLElement;
  selection?: SelectionSnapshot;
};

type SelectableInput = HTMLInputElement | HTMLTextAreaElement;

const preservedMoveRoots = new Set<Node>();

const isSelectableInput = (element: Element): element is SelectableInput =>
  element instanceof HTMLTextAreaElement || (element instanceof HTMLInputElement && element.selectionStart !== null);

const captureSelection = (element: HTMLElement): SelectionSnapshot | undefined => {
  if (!isSelectableInput(element)) return undefined;

  return {
    start: element.selectionStart,
    end: element.selectionEnd,
    direction: element.selectionDirection ?? SELECTION_DIRECTION.None,
  };
};

const captureFocus = (nodes: Node[]): FocusSnapshot | undefined => {
  const active = document.activeElement;
  if (!(active instanceof HTMLElement) || !nodes.some(node => node.contains(active))) return undefined;

  return { element: active, selection: captureSelection(active) };
};

const restoreSelection = ({ element, selection }: FocusSnapshot): void => {
  if (!selection || selection.start === null || selection.end === null) return;
  if (!isSelectableInput(element)) return;

  element.setSelectionRange(selection.start, selection.end, selection.direction);
};

const restoreFocus = (snapshot: FocusSnapshot | undefined): void => {
  if (!snapshot || !snapshot.element.isConnected) return;

  if (document.activeElement !== snapshot.element) snapshot.element.focus({ preventScroll: true });
  restoreSelection(snapshot);
};

const insertUnlessInPlace = (parent: Node, node: Node, reference: Node): void => {
  if (node.parentNode !== parent || node.nextSibling !== reference) parent.insertBefore(node, reference);
};

const insertNodesBefore = (parent: Node, nodes: Node[], anchor: Node): void => {
  let reference = anchor;
  for (const node of [...nodes].reverse()) {
    if (reference.parentNode !== parent) break;

    insertUnlessInPlace(parent, node, reference);
    // An insertion callback can remove the moved root; keep the last surviving reference for its siblings.
    if (node.parentNode === parent) reference = node;
  }
};

/** True while `node` or one of its ancestors is being moved by `moveNodesBefore`. */
export const isNodeInPreservedMove = (node: Node): boolean => {
  let current: Node | null = node;

  while (current) {
    if (preservedMoveRoots.has(current)) return true;
    current = current.parentNode;
  }

  return false;
};

/**
 * Inserts `nodes` (in order) before `anchor`. Nodes that stay connected through the move are marked as a preserved
 * move for the duration of the synchronous insert only; nodes already in place are left untouched.
 */
export const moveNodesBefore = (nodes: Node[], anchor: Node): void => {
  const parent = anchor.parentNode;
  if (!parent) return;

  const focus = captureFocus(nodes);
  const preserved = anchor.isConnected ? nodes.filter(node => node.isConnected) : [];
  preserved.forEach(node => preservedMoveRoots.add(node));

  try {
    insertNodesBefore(parent, nodes, anchor);
  } finally {
    preserved.forEach(node => preservedMoveRoots.delete(node));
  }

  // Focus handlers can remove the subtree; those removals must run outside move protection.
  restoreFocus(focus);
};
